import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Toast from '../components/Toast';
import ExerciseInfoModal from '../components/ExerciseInfoModal';
import { DAYS, DAYS_ES, DAY_LABELS, CATEGORY_LABELS } from '../constants';
import './Routines.css';

export default function Routines() {
  const { user, impersonating } = useAuth();
  const [activeDay,   setActiveDay]   = useState(0);
  const [routineMap,  setRoutineMap]  = useState({});
  const [loading,     setLoading]     = useState(true);
  const [showPicker,  setShowPicker]  = useState(false);
  const [pickerSearch,setPickerSearch]= useState('');
  const [pickerExercises, setPickerExercises] = useState([]);
  const [pickerLoading,   setPickerLoading]   = useState(false);
  const [pickerLoadingMore, setPickerLoadingMore] = useState(false);
  const [selectedEx,  setSelectedEx]  = useState(null);
  const [infoEx,      setInfoEx]      = useState(null);
  const [addForm,     setAddForm]     = useState({ sets: 3, reps: 10, rest_seconds: 60, order_index: 1 });
  const [saving,      setSaving]      = useState(false);
  const [toast,       setToast]       = useState(null);
  const [editing,     setEditing]     = useState(null);
  const [editForm,    setEditForm]    = useState({ sets: 3, reps: 10, rest_seconds: 60 });
  const [copyTarget,  setCopyTarget]  = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [infoExercise, setInfoExercise] = useState(null);

  const pickerSentinelRef    = useRef(null);
  const pickerSearchTimeout  = useRef(null);
  const pickerParamsRef      = useRef({ search: '' });
  const pickerPageRef        = useRef(1);
  const pickerHasMoreRef     = useRef(false);
  const pickerLoadingMoreRef = useRef(false);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const routineRes = await api.get('/routines');
      const map = {};
      DAYS.forEach((_, i) => { map[i] = []; });
      const routineData = routineRes.data.routine || routineRes.data || [];
      routineData.forEach(dayEntry => {
        const dayIdx = typeof dayEntry.day === 'number' ? dayEntry.day : DAYS.indexOf(dayEntry.day_name);
        if (dayIdx >= 0) map[dayIdx] = dayEntry.exercises || [];
      });
      Object.keys(map).forEach(dayIdx => {
        map[dayIdx].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
      });
      setRoutineMap(map);
    } catch {
      showToast('Error al cargar las rutinas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const fetchPickerExercises = useCallback(async ({ search: s, page: p }, append = false) => {
    if (!append) setPickerLoading(true);
    else { setPickerLoadingMore(true); pickerLoadingMoreRef.current = true; }
    try {
      const res = await api.get('/exercises', {
        params: { ...(s && { search: s }), sort: 'alpha', page: p, per_page: 100 },
      });
      const d = res.data;
      if (append) setPickerExercises(prev => [...prev, ...(d.exercises || [])]);
      else setPickerExercises(d.exercises || []);
      pickerHasMoreRef.current = d.has_more || false;
      pickerPageRef.current = p;
      pickerParamsRef.current = { search: s };
    } catch {}
    finally {
      setPickerLoading(false);
      setPickerLoadingMore(false);
      pickerLoadingMoreRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!showPicker || selectedEx || infoEx) return;
    const sentinel = pickerSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && pickerHasMoreRef.current && !pickerLoadingMoreRef.current) {
        fetchPickerExercises({ search: pickerParamsRef.current.search, page: pickerPageRef.current + 1 }, true);
      }
    }, { rootMargin: '200px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showPicker, selectedEx, infoEx, pickerLoading, fetchPickerExercises]);

  function openPicker() {
    setPickerSearch('');
    setSelectedEx(null);
    setInfoEx(null);
    fetchPickerExercises({ search: '', page: 1 });
    setShowPicker(true);
  }

  function handlePickerSearch(val) {
    setPickerSearch(val);
    clearTimeout(pickerSearchTimeout.current);
    pickerSearchTimeout.current = setTimeout(() => {
      fetchPickerExercises({ search: val, page: 1 });
    }, 300);
  }

  function selectExercise(ex) {
    setSelectedEx(ex);
    setInfoEx(null);
    setAddForm({
      sets: ex.category === 'cardio' ? 1 : 3,
      reps: ex.category === 'cardio' ? '20' : '10',
      rest_seconds: ex.category === 'cardio' ? 0 : 60,
      order_index: dayItems.length,
    });
  }

  const currentDayName = DAYS_ES[activeDay];
  const dayItems = routineMap[activeDay] || [];

  async function handleDelete(routineId) {
    setConfirmDelete(routineId);
  }

  async function confirmAndDelete() {
    if (!confirmDelete) return;
    try {
      await api.delete(`/routines/${confirmDelete}`);
      showToast('¡Ejercicio eliminado!', 'success');
      setConfirmDelete(null);
      loadAll();
    } catch {
      showToast('Error al eliminar el ejercicio');
      setConfirmDelete(null);
    }
  }

  async function handleMove(item, direction) {
    const items = [...dayItems];
    const idx = items.findIndex(i => i.id === item.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    try {
      await Promise.all([
        api.put(`/routines/${item.id}`, { order_index: swapIdx }),
        api.put(`/routines/${items[swapIdx].id}`, { order_index: idx }),
      ]);
      loadAll();
    } catch {
      showToast('Error al reordenar');
    }
    setMoveTarget(null);
  }

  async function handleMoveToDay(item, targetDay) {
    setSaving(true);
    try {
      await api.put(`/routines/${item.id}`, { day_of_week: targetDay });
      showToast(`Ejercicio movido a ${DAYS_ES[targetDay]}`, 'success');
      setMoveTarget(null);
      loadAll();
    } catch {
      showToast('Error al mover el ejercicio');
    } finally {
      setSaving(false);
    }
  }

  function startEdit(item) {
    setEditing(item.id);
    setEditForm({ sets: item.sets, reps: item.reps, rest_seconds: item.rest_seconds });
  }

  async function handleEditSave(routineId) {
    const nSets = parseInt(editForm.sets, 10);
    if (!nSets || nSets < 1) {
      showToast('Las series deben ser al menos 1');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/routines/${routineId}`, { ...editForm, sets: nSets });
      showToast('¡Actualizado!', 'success');
      setEditing(null);
      loadAll();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  }

  async function handleCopyDay(targetDay) {
    if (dayItems.length === 0) return;
    setSaving(true);
    try {
      await Promise.all(dayItems.map(item =>
        api.post('/routines', {
          exercise_id: item.exercise_id,
          day_of_week: targetDay,
          sets: item.sets,
          reps: item.reps,
          rest_seconds: item.rest_seconds,
          order_index: item.order_index,
        })
      ));
      showToast(`¡Rutina copiada a ${DAYS_ES[targetDay]}!`, 'success');
      setCopyTarget(null);
      loadAll();
    } catch {
      showToast('Error al copiar la rutina');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddExercise(e) {
    e.preventDefault();
    if (!selectedEx) return;
    const numSets = parseInt(addForm.sets, 10);
    if (!numSets || numSets < 1) {
      showToast('Las series deben ser al menos 1');
      return;
    }
    setSaving(true);
    try {
      await api.post('/routines', {
        exercise_id:  selectedEx.id,
        day_of_week:  activeDay,
        sets:         numSets,
        reps:         parseInt(addForm.reps, 10),
        rest_seconds: parseInt(addForm.rest_seconds, 10),
        order_index:  dayItems.length,
      });
      showToast('¡Ejercicio agregado!', 'success');
      setShowPicker(false);
      setSelectedEx(null);
      setPickerSearch('');
      loadAll();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al agregar el ejercicio');
    } finally {
      setSaving(false);
    }
  }

  async function loadTemplates() {
    setLoadingTemplates(true);
    try {
      const res = await api.get('/routines/templates');
      setTemplates(res.data.templates || []);
    } catch {
      showToast('Error al cargar plantillas');
    } finally {
      setLoadingTemplates(false);
    }
  }

  async function handleApplyTemplate(templateId) {
    setApplyingTemplate(templateId);
    try {
      const res = await api.post('/routines/apply-template', {
        template_id: templateId,
        day_of_week: activeDay,
      });
      showToast(res.data.message || '¡Plantilla aplicada!', 'success');
      setShowTemplatePicker(false);
      loadAll();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al aplicar la plantilla');
    } finally {
      setApplyingTemplate(null);
    }
  }

  function openTemplatePicker() {
    loadTemplates();
    setShowTemplatePicker(true);
  }

  const totalEstimatedDuration = dayItems.reduce((sum, item) => {
    const sets = parseInt(item.sets, 10) || 3;
    const rest = parseInt(item.rest_seconds, 10) || 60;
    return sum + (sets * rest);
  }, 0);

  const durationMin = Math.floor(totalEstimatedDuration / 60);
  const durationSec = totalEstimatedDuration % 60;

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}
      <div className="page">
        <div className="page-header">
          <div className="page-title">Rutinas</div>
        </div>

        <div className="day-tabs">
          {DAY_LABELS.map((label, i) => (
            <button
              key={label}
              className={'day-tab' + (activeDay === i ? ' active' : '')}
              onClick={() => { setActiveDay(i); setEditing(null); setCopyTarget(null); }}
              type="button"
            >
              <span className="day-tab-name">{label}</span>
              <span style={{ fontSize: '0.625rem', color: activeDay === i ? '#e94560' : '#4a4a6a' }}>
                {(routineMap[i] || []).length}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="px-16">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="skeleton skeleton-card" />
            ))}
          </div>
        ) : (
          <div className="px-16">
            {dayItems.length > 0 && (
              <div className="routine-summary">
                <span>{dayItems.length} ejercicio{dayItems.length > 1 ? 's' : ''}</span>
                <span className="routine-summary-dot">·</span>
                <span>~{durationMin}:{String(durationSec).padStart(2, '0')} est.</span>
                <span className="routine-summary-dot">·</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setCopyTarget(copyTarget === activeDay ? null : activeDay)}
                  style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                >
                  {copyTarget === activeDay ? 'Cancelar copia' : 'Copiar a otro día'}
                </button>
              </div>
            )}

            {copyTarget !== null && (
              <div className="copy-day-bar">
                <span className="text-sm text-muted">Copiar rutina de <b>{DAYS_ES[activeDay]}</b> a:</span>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {DAYS_ES.map((name, i) => i !== activeDay && (
                    <button key={i} type="button" className="chip" onClick={() => handleCopyDay(i)} disabled={saving}>
                      {DAY_LABELS[i]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {dayItems.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px 0' }}>
                <div className="empty-state-icon">📅</div>
                <div className="empty-state-title">Día de descanso</div>
                <div className="empty-state-text">No hay ejercicios para {currentDayName}.</div>
              </div>
            ) : (
              <div className="routine-list">
                {dayItems.map((item, idx) => (
                  <div key={item.id} className="routine-item card">
                    {editing === item.id ? (
                      <div className="routine-edit-inline">
                        <div className="routine-ex-name">{item.exercise?.name}</div>
                        {item.exercise?.category === 'cardio' ? (
                          <div className="form-group" style={{ marginBottom: 0, marginTop: 8 }}>
                            <label className="form-label" style={{ fontSize: '0.7rem' }}>Tiempo (min)</label>
                            <input className="form-input form-input-sm" type="number" min="1" value={editForm.reps}
                              onChange={e => setEditForm(f => ({ ...f, reps: parseInt(e.target.value, 10) || 1 }))} />
                          </div>
                        ) : (
                          <div className="form-row-3" style={{ marginTop: 8 }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label className="form-label" style={{ fontSize: '0.7rem' }}>Series</label>
                              <input className="form-input form-input-sm" type="number" min="1" value={editForm.sets}
                                onChange={e => setEditForm(f => ({ ...f, sets: parseInt(e.target.value, 10) || 1 }))} />
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label className="form-label" style={{ fontSize: '0.7rem' }}>Reps</label>
                              <input className="form-input form-input-sm" type="number" min="1" value={editForm.reps}
                                onChange={e => setEditForm(f => ({ ...f, reps: parseInt(e.target.value, 10) || 1 }))} />
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label className="form-label" style={{ fontSize: '0.7rem' }}>Descanso (s)</label>
                              <input className="form-input form-input-sm" type="number" min="0" value={editForm.rest_seconds}
                                onChange={e => setEditForm(f => ({ ...f, rest_seconds: parseInt(e.target.value, 10) || 0 }))} />
                            </div>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <button type="button" className="btn btn-xs btn-primary" onClick={() => handleEditSave(item.id)} disabled={saving}>
                            {saving ? '...' : 'Guardar'}
                          </button>
                          <button type="button" className="btn btn-xs btn-secondary" onClick={() => setEditing(null)}>Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ width: '100%' }}>
                        <div className="routine-item-row">
                          <div className="routine-item-left">
                            <div className="routine-order">{idx + 1}</div>
                            <div>
                              <div className="routine-ex-name">{item.exercise?.name}</div>
                              <div className="routine-ex-meta text-muted text-sm">
                                {item.exercise?.category === 'cardio' ? (
                                  <>1 serie · {item.reps} min</>
                                ) : (
                                  <>{item.sets} series × {item.reps} repeticiones</>
                                )}
                                {item.rest_seconds ? ` · ${item.rest_seconds}s descanso` : ''}
                              </div>
                              {item.exercise?.category && (
                                <span className={`badge badge-${(item.exercise.category || 'other').toLowerCase()}`} style={{ marginTop: 4, display: 'inline-block' }}>
                                  {CATEGORY_LABELS[(item.exercise.category || 'other').toLowerCase()] || item.exercise.category}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="routine-item-actions">
                            {item.exercise?.gif_url && (
                              <button className="routine-move-btn" onClick={() => setInfoExercise(item.exercise)} aria-label="Información" type="button" style={{ fontSize: '0.7rem', color: '#a0a0b0' }}>ℹ</button>
                            )}
                            <button className="routine-move-btn" onClick={() => startEdit(item)} aria-label="Editar" type="button" style={{ fontSize: '0.7rem' }}>✏️</button>
                            <button className="routine-move-btn" onClick={() => handleMove(item, -1)} disabled={idx === 0} aria-label="Subir" type="button">▲</button>
                            <button className="routine-move-btn" onClick={() => handleMove(item, 1)} disabled={idx === dayItems.length - 1} aria-label="Bajar" type="button">▼</button>
                            <button className="routine-move-btn" onClick={() => setMoveTarget(moveTarget === item.id ? null : item.id)} aria-label="Mover a otro día" type="button" style={{ fontSize: '0.65rem' }}>↗</button>
                            {confirmDelete === item.id ? (
                              <div className="routine-confirm-delete">
                                <span className="text-xs">¿Eliminar?</span>
                                <button type="button" className="btn btn-xs btn-danger" onClick={confirmAndDelete}>Sí</button>
                                <button type="button" className="btn btn-xs btn-secondary" onClick={() => setConfirmDelete(null)}>No</button>
                              </div>
                            ) : (
                              <button className="routine-delete-btn" onClick={() => handleDelete(item.id)} aria-label="Eliminar" type="button">×</button>
                            )}
                          </div>
                        </div>
                        {moveTarget === item.id && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                            {DAYS_ES.map((name, i) => i !== activeDay && (
                              <button key={i} type="button" className="chip" style={{ fontSize: '0.6rem', padding: '3px 8px' }}
                                onClick={() => handleMoveToDay(item, i)} disabled={saving}>
                                {DAY_LABELS[i]}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button className="btn btn-secondary btn-full mt-16" onClick={openPicker} type="button">
              + Agregar ejercicio a {currentDayName}
            </button>

            {(user?.is_admin || impersonating) && (
              <button className="btn btn-primary btn-full mt-8" onClick={openTemplatePicker} type="button">
                📋 Cargar plantilla a {currentDayName}
              </button>
            )}
          </div>
        )}
      </div>

      {showTemplatePicker && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowTemplatePicker(false)}>
          <div className="modal-sheet">
            <div className="modal-handle" />
            <div className="modal-title">Aplicar plantilla a {currentDayName}</div>
            {loadingTemplates ? (
              <div className="loading-container"><div className="spinner" /></div>
            ) : templates.length === 0 ? (
              <div className="text-muted text-sm" style={{ textAlign: 'center', padding: '20px 0' }}>
                No hay plantillas disponibles. Créalas desde Admin &gt; Plantillas.
              </div>
            ) : (
              <div className="picker-list">
                {templates.map(t => (
                  <button key={t.id} className="picker-item" onClick={() => handleApplyTemplate(t.id)} disabled={applyingTemplate === t.id} type="button">
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#eaeaea', fontSize: '0.9375rem' }}>{t.name}</div>
                      {t.description && <div className="text-muted text-xs" style={{ marginTop: 2 }}>{t.description}</div>}
                      <div className="text-muted text-xs" style={{ marginTop: 2 }}>{t.exercise_count} ejercicio{t.exercise_count !== 1 ? 's' : ''}</div>
                    </div>
                    <span style={{ color: '#e94560', fontWeight: 600, fontSize: '0.8125rem' }}>
                      {applyingTemplate === t.id ? '...' : 'Aplicar'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showPicker && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowPicker(false)}>
          <div className="modal-sheet">
            <div className="modal-handle" />
            <div className="modal-title">Agregar a {currentDayName}</div>

            {selectedEx ? (
              <form onSubmit={handleAddExercise}>
                <div className="selected-ex-banner">
                  <span className={`badge badge-${(selectedEx.category || 'other').toLowerCase()}`}>
                    {CATEGORY_LABELS[(selectedEx.category || 'other').toLowerCase()] || selectedEx.category || 'Otro'}
                  </span>
                  <span className="fw-600">{selectedEx.name}</span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedEx(null)}>Cambiar</button>
                </div>
                {selectedEx.category === 'cardio' ? (
                  <div className="form-group">
                    <label className="form-label">Tiempo (min)</label>
                    <input className="form-input" type="number" min="1" value={addForm.reps}
                      onChange={e => setAddForm(f => ({ ...f, reps: e.target.value }))} required />
                  </div>
                ) : (
                  <div className="form-row-3">
                    <div className="form-group">
                      <label className="form-label">Series</label>
                      <input className="form-input" type="number" min="1" value={addForm.sets}
                        onChange={e => setAddForm(f => ({ ...f, sets: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Repeticiones</label>
                      <input className="form-input" type="number" min="1" value={addForm.reps}
                        onChange={e => setAddForm(f => ({ ...f, reps: e.target.value }))} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Descanso (s)</label>
                      <input className="form-input" type="number" min="0" value={addForm.rest_seconds}
                        onChange={e => setAddForm(f => ({ ...f, rest_seconds: e.target.value }))} />
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowPicker(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>
                    {saving ? <span className="spinner spinner-sm" /> : 'Agregar a la rutina'}
                  </button>
                </div>
              </form>
            ) : infoEx ? (
              <div>
                <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }} onClick={() => setInfoEx(null)}>
                  ← Volver
                </button>
                {infoEx.gif_url && (
                  <img
                    src={infoEx.gif_url}
                    alt={infoEx.name}
                    style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10, marginBottom: 12 }}
                  />
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                  <span className={`badge badge-${(infoEx.category || 'other').toLowerCase()}`}>
                    {CATEGORY_LABELS[(infoEx.category || 'other').toLowerCase()] || infoEx.category || 'Otro'}
                  </span>
                  {infoEx.is_global && <span className="badge badge-admin">Admin</span>}
                  {infoEx.is_bodyweight && <span className="badge badge-admin" style={{ fontSize: '0.6rem' }}>Sin peso</span>}
                </div>
                <div className="fw-600" style={{ color: '#eaeaea', fontSize: '1rem', marginBottom: 4 }}>{infoEx.name}</div>
                {infoEx.muscle_group && (
                  <div className="text-muted text-sm" style={{ marginBottom: 8 }}>{infoEx.muscle_group}</div>
                )}
                {infoEx.description && (
                  <div className="text-muted text-sm" style={{ marginBottom: 12 }}>{infoEx.description}</div>
                )}
                <button type="button" className="btn btn-primary btn-full" onClick={() => selectExercise(infoEx)}>
                  Agregar a la rutina
                </button>
              </div>
            ) : (
              <>
                <div style={{ position: 'relative', marginBottom: 12 }}>
                  <span className="search-icon" style={{ left: 12 }}>🔍</span>
                  <input
                    className="search-input"
                    style={{ paddingLeft: 36 }}
                    placeholder="Buscar ejercicios..."
                    value={pickerSearch}
                    onChange={e => handlePickerSearch(e.target.value)}
                  />
                </div>
                <div className="picker-list">
                  {pickerLoading ? (
                    <div className="loading-container" style={{ padding: '20px 0' }}><div className="spinner" /></div>
                  ) : pickerExercises.length === 0 ? (
                    <div className="text-muted text-sm" style={{ textAlign: 'center', padding: '20px 0' }}>
                      No se encontraron ejercicios
                    </div>
                  ) : (
                    <>
                      {pickerExercises.map(ex => (
                        <div key={ex.id} className="picker-item" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <button
                            type="button"
                            style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', flexWrap: 'wrap' }}
                            onClick={() => selectExercise(ex)}
                          >
                            <span className={`badge badge-${(ex.category || 'other').toLowerCase()}`}>
                              {CATEGORY_LABELS[(ex.category || 'other').toLowerCase()] || ex.category || 'Otro'}
                            </span>
                            <span className="picker-item-name" style={{ flex: 1 }}>{ex.name}</span>
                            {ex.is_global && <span className="badge badge-admin" style={{ fontSize: '0.6rem', padding: '1px 5px' }}>Admin</span>}
                            {ex.is_bodyweight && <span className="badge badge-admin" style={{ fontSize: '0.6rem', padding: '1px 5px' }}>Sin peso</span>}
                          </button>
                          <button
                            type="button"
                            onClick={() => setInfoEx(ex)}
                            style={{ flexShrink: 0, background: 'none', border: '1px solid #2a2a4a', borderRadius: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0a0b0', cursor: 'pointer', fontSize: '0.8rem' }}
                            aria-label="Ver información"
                          >
                            i
                          </button>
                        </div>
                      ))}
                      <div ref={pickerSentinelRef} style={{ height: 1 }} />
                      {pickerLoadingMore && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                          <div className="spinner spinner-sm" />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <ExerciseInfoModal exercise={infoExercise} onClose={() => setInfoExercise(null)} />
    </Layout>
  );
}
